import 'ol/ol.css';

import Map from 'ol/Map';
import Tile from 'ol/layer/Tile';
import View from 'ol/View';
import {Attribution, defaults as defaultControls} from 'ol/control';
import {DragPan} from 'ol/interaction';
import {Viewer} from "../main";
import {OSM, XYZ} from "ol/source";
import {transform} from 'ol/proj';
import {PanoramicImage, PlanarImage} from '../cameras';
import {Cluster, Vector as VectorSource} from "ol/source";
import {Vector as VectorLayer} from "ol/layer";
import {
  Circle as CircleStyle,
  RegularShape as RegularShapeStyle,
  Fill, Stroke, Style, Text
} from "ol/style";
import {Point, Polygon} from "ol/geom";
import {Feature} from "ol";
import {boundingExtent} from 'ol/extent';
import LocalScene, {
  DataProjectionCoordinate,
  LonLatCoordinate,
  SceneCoordinate
} from '../projections';
import {Vector3} from 'three';
import { Projection } from '../../../redux/projections-slice';
import { OrthoMosaicList } from '.';
import { CameraImage } from '../cameras';
import { AerialViewState } from '../../../redux/camera-slice';
import onresize from "resize-event";
import { ContextData, showContextMenu } from '../../context-menu';
import { clearAerialTooltip, setAerialState } from '../../viewer';
import { ChangeDetector } from '../utilities';

export type Basemap = 'open-street-maps' | 'arcgis-world' | 'arcgis-street';

const BasemapLayer = (id: Basemap, text, source) => {
  return new Tile({
    id: id,
    text: text,
    type: 'basemap',
    source: source
  });
};

class BaseCameraFeature extends Feature {
  public cameraID;
  public fromJSON;
  public cameraCSV;

  constructor(opts) {
    super(opts);
    this.cameraID = opts.cameraID;
    this.cameraCSV = opts.cameraCSV;
    this.fromJSON = opts.fromJSON;
  }
}

class PanoramicFeature extends BaseCameraFeature {
  constructor(opts) {
    super(opts);
  }
}

class PlanarFeature extends BaseCameraFeature {
  constructor(opts) {
    super(opts);
  }
}

export class TagFeature extends Feature {
  public tagID: string;
  //public name: string;
  public texture: string;
  //public comment: string;
  public position: number[];

  constructor(tagID, texture, position: number[], opts) {
    super(opts);
    this.tagID = tagID;
    this.texture = texture;
    this.position = position;
  }
}

class ViewTriangle {
  private aerial: AerialMap;
  private viewer: Viewer;
  private map: Map;
  private feature;
  private layer;

  constructor(parent, map, viewer) {
    this.aerial = parent;
    this.viewer = viewer;
    this.map = map;

    this.generateSource();
  }

  get camera() {
    return this.viewer.camera;
  }

  generateSource() {
    // Adding our view triangle source
    const source = new VectorSource({
      wrapX: false
    });
    const polygon = new Polygon([[0,0], [0,0]]);
    const feature = new Feature(polygon);
    source.addFeature(feature);

    const layer = new VectorLayer({
      zIndex: 1,
      visible: false,
      type: "triangle",
      source: source,
      style: new Style({
        stroke: new Stroke({
          color: "rgba(0,0,0,0.5)",
          width: 1.0
        }),
        fill: new Fill({
          color: "rgba(64,244,155,0.5)"
        })
      })
    });

    this.map.addLayer(layer);
    this.feature = feature;
    this.layer = layer;
  }

  rotateViewMarker(lookat) {
    // Updating feature geometry
    let ring = this.generateMarkerCoordinates(lookat);
    let polygon = new Polygon([ring]);
    polygon.transform('EPSG:4326', 'EPSG:3857');
    this.feature.setGeometry(polygon);
  }

  generateMarkerCoordinates(lookat) {
    // Set up additional constants
    let height = this.map.getTargetElement().clientHeight;
    let coneAngle = 0.8 * 0.5 * (this.camera.fov * Math.PI / 180);
    let axis = new Vector3(0, 0, 1);

    // Cone length is measured in meters based on aerial height
    let meterPerPixel = this.map.getView().getResolution();
    let coneLength = height * 0.10 * meterPerPixel;
    let maxConeLength = 1000000;
    if (coneLength > maxConeLength) {
      coneLength = 0;
    }

    // Center position
    let v0 = this.camera.position;

    // Set triangle size
    let vector = new Vector3(lookat.x, lookat.y, 0);
    vector.setLength(coneLength);

    // First triangle corner
    let v1 = vector.clone()
      .applyAxisAngle(axis, coneAngle)
      .add(v0);

    // Second triangle corner
    let v2 = vector.clone()
      .applyAxisAngle(axis, -coneAngle)
      .add(v0);

    v0 = new SceneCoordinate(v0).toLonLat();
    v1 = new SceneCoordinate(v1).toLonLat();
    v2 = new SceneCoordinate(v2).toLonLat();

    let ring = [
      [v1.x, v1.y],
      [v2.x, v2.y],
      [v0.x, v0.y],
      [v1.x, v1.y]
    ];

    return ring;
  }

  clear() {
    this.layer.setVisible(false);
  }

  update(changeDetector: ChangeDetector) {
    if (!LocalScene.initialized || !changeDetector.changed) return;

    // Update view marker geometry
    this.rotateViewMarker(this.viewer.lookatVector);
    this.layer.setVisible(true);
  }
}

export class AerialMap {
  private viewer: Viewer;
  public containerElement;
  public map: Map;
  public orthoList: OrthoMosaicList;
  private triangle;
  private maxLayerZoom = 20;
  private maxMapZoomLevel = 28;
  private maxCameraZoomLevel = 23;
  private styleCache = {};
  private pointsSource: VectorSource;
  private clusterSource: Cluster;
  private clusterLayer: VectorLayer
  public activeCamera: PanoramicImage;
  private activeCameraLayer;
  private clusterDistance = 10;
  private clusterCircleSize = 5.0;
  private camerasToIterate = [];
  private maxIterationTime = 5;
  public calculating = false;
  private features = [];
  private cameras = [];
  private observer;

  public mapSystem = {
    string: "EPSG:3857",
    transform: null
  } as Projection;

  constructor(viewer: Viewer, container, props) {
    this.viewer = viewer;
    this.containerElement = container;

    this.initMap();
    this.initEvents();
    this.initResize();
    this.initDataLayers();

    this.orthoList = new OrthoMosaicList(this);
  }

  get defaultLayers() {
    const arcGISBase = "https://services.arcgisonline.com/ArcGIS/rest/services";

    const layers = [
      BasemapLayer("open-street-maps", "OpenStreetMaps", new OSM({
        maxZoom: this.maxLayerZoom
      })),

      BasemapLayer("arcgis-world", "ArcGIS World", new XYZ({
        url: `${arcGISBase}/World_Imagery/MapServer/tile/{z}/{y}/{x}.png`,
        maxZoom: this.maxLayerZoom
      })),

      BasemapLayer("arcgis-world", "ArcGIS World", new XYZ({
        attributions: 'Powered by <a href="https://www.esri.com/en-us/home" target="_blank">Esri</a>',
        url: `${arcGISBase}/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}.png`,
        maxZoom: this.maxLayerZoom
      })),

      BasemapLayer("arcgis-street", "ArcGIS Street", new XYZ({
        attributions: 'Powered by <a href="https://www.esri.com/en-us/home" target="_blank">Esri</a>',
        url: `${arcGISBase}/World_Street_Map/MapServer/tile/{z}/{y}/{x}.png`,
        maxZoom: this.maxLayerZoom
      })),
    ];

    return layers;
  }

  get cameraList() {
    return this.viewer.cameraList;
  }

  get width() {
    return this.containerElement.scrollWidth;
  }

  get height() {
    return this.containerElement.scrollHeight;
  }

  get basemapIds() {
    let layers = [];
    this.basemapLayers.forEach(layer => {
      let id = layer.get("id");
      let text = layer.get("text");
      let ids = layers.map(x => x.id);
      if (ids.indexOf(id) !== -1) {
        return;
      }
      layers.push({id, text});
    });

    return layers;
  }

  get basemapLayers() {
    let layers = this.map.getLayers();
    return layers.array_.filter(layer => layer.get("type") === "basemap");
  }

  get activeBasemap() {
    let basemapID = null;
    this.basemapLayers.forEach(layer => {
      let isVisible = layer.getVisible();
      if (isVisible) {
        basemapID = layer.get("id");
      }
    });

    return basemapID;
  }

  get aerialState() {
    const view = this.map.getView();
    const currentBasemap = this.basemapLayers
      .find(x => x.getVisible())
      .get("id");

    return {
      center: view.getCenter(),
      zoom: view.getZoom(),
      rotation: view.getRotation(),
      basemap: currentBasemap
    } as AerialViewState;
  }

  get anyToolEnabled() {
    return this.orthoLabels.enabled
      || this.measurements.enabled;
  }

  get orthoLabels() {
    return this.viewer.imageLabels.orthoLabels;
  }

  get localAligner() {
    return this.viewer.controls.localAligner;
  }

  get measurements() {
    return this.viewer.measurements;
  }

  get aerialTags() {
    return this.viewer.tags.aerialController;
  }

  get element() {
    return this.map.getTargetElement();
  }

  resetView(state = {} as AerialViewState) {
    const view = this.map.getView();

    const center = state.center
      ? state.center
      : transform([0, 0], 'EPSG:4326', 'EPSG:3857');

    const zoom = state.zoom
      ? state.zoom
      : 0;

    const rotation = state.rotation
      ? state.rotation
      : 0;

    const basemap = state.basemap
      ? state.basemap
      : this.basemapLayers[0].get("id");

    view.setCenter(center);
    view.setZoom(zoom);
    view.setRotation(rotation);
    this.setVisibleBasemap(basemap);
  }

  initMap() {
    const attribution = new Attribution({
      collapsible: true,
    });

    this.map = new Map({
      layers: this.defaultLayers,
      target: this.containerElement,
      fractionalZoom: false,
      controls: defaultControls({
        zoom: false,
        attribution: false,
        rotate: true
      }).extend([attribution]),
      view: new View({
        maxZoom: this.maxMapZoomLevel
      })
    });

    this.triangle = new ViewTriangle(this, this.map, this.viewer);

    this.resetView();
    this.setActiveCamera();
  }

  initResize() {
    this.observer = onresize(this.containerElement, () => {
      this.handleWindowResize();
    });

    this.handleWindowResize();
  }

  initEvents() {
    this.onMapClick = this.onMapClick.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onDoubleClick = this.onDoubleClick.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.updateSavedState = this.updateSavedState.bind(this);

    this.map.on('click', this.onMapClick);
    this.map.on('dblclick', this.onDoubleClick);
    this.map.on('pointermove', this.onMouseMove);
    this.map.on('moveend', this.updateSavedState);
    this.element.addEventListener('mouseup', this.onMouseUp, false);
  }

  removeEvents() {
    this.map.un('click', this.onMapClick);
    this.map.un('dblclick', this.onDoubleClick);
    this.map.un('pointermove', this.onMouseMove);
    this.map.un('moveend', this.updateSavedState);
    this.element.removeEventListener('mouseup', this.onMouseUp, false);
  }

  initDataLayers() {
    // Create cluster layer for images
    this.pointsSource = new VectorSource({
      wrapX: false
    });

    this.clusterSource = new Cluster({
      distance: this.clusterDistance,
      source: this.pointsSource,
    });

    this.clusterLayer = new VectorLayer({
      zIndex: 1,
      source: this.clusterSource,
      style: feature => {
        return this.clusterStyle(feature);
      },
    });

    this.map.addLayer(this.clusterLayer);

    // Activate camera source
    this.activeCameraLayer = new VectorLayer({
      zIndex: 1,
      visible: false,
      source: new VectorSource({
        wrapX: false,
        features: [new Feature({
          geometry: new Point([0,0])
        })]
      }),
      style: this.styleGenerator(1, true)
    });

    this.map.addLayer(this.activeCameraLayer);
  }

  onDoubleClick(event) {
    this.orthoLabels.onDoubleClick(event);
  }

  onMapClick(event) {
    const mapClicked = this.orthoLabels.onMapClick(event)
      || this.localAligner.onMapClick(event);

    if (mapClicked) {
      return;
    }

    let features = this.aerialTags.hovering
      ? this.aerialTags.getFeatureForEvent(event)
      : this.getFeatureForEvent(event);

    // No features were found
    if (features.length === 0) {
      return;
    }

    const currentView = this.map.getView();
    const currentZoom = Math.ceil(currentView.getZoom());
    const maxZoomReached = currentZoom >= this.maxCameraZoomLevel;
    const singleFeature = features.length === 1;

    if (singleFeature || maxZoomReached) {
      const feature = features[0];

      if (feature instanceof TagFeature) {
        this.viewer.jumpToTag(feature.position, false);
      } else if (feature instanceof BaseCameraFeature) {
        this.viewer.setCameraVisible(feature.cameraID);
      }

      return;
    }

    // Zoom to cluster with 50% boundary
    let coordinates = [...features].map((x: Feature) => x.getGeometry().getCoordinates());
    let extent = boundingExtent(coordinates);
    let mapSize = this.map.getSize();

    this.map.getView().fit(extent, {
      size: [mapSize[0] * 0.5, mapSize[1] * 0.5],
      nearest: false,
      duration: 250,
    });

    clearAerialTooltip();
  }

  onMouseUp(event) {
    // Add custom attributes to event
    event.isLeftClick = event.button === 0;
    event.isRightClick = event.button === 2;

    const mouseClicked = this.orthoLabels.onMouseUp(event)
      || this.localAligner.onMouseUp(event);

    if (mouseClicked) {
      return;
    }

    if (event.isRightClick) {
      this.openContextMenu(event);
    }
  }

  onMouseMove(event) {
    if (this.orthoLabels.enabled) {
      this.orthoLabels.onMouseMove(event);
    } else if (this.measurements.enabled) {
      this.measurements.onMouseMoveAerial(event);
    } else if (this.localAligner.enabled) {
      this.localAligner.onMouseMoveAerial();
    } else {
      this.aerialTags.onMouseMove(event);
    }
  }

  updateSavedState() {
    setAerialState(this.aerialState);
  }

  openContextMenu(event) {
    if (this.anyToolEnabled) {
      return showContextMenu(event, true);
    }

    // Tag attributes
    const pixel = [event.offsetX, event.offsetY];
    const selectedTags = this.aerialTags
      .getFeatureForEvent({pixel})
      .map(tag => tag.tagID);

    // New tag coordinate
    const mapCoordinate = this.map.getCoordinateFromPixel(pixel);
    const lonlat = transform(mapCoordinate, 'EPSG:3857', 'EPSG:4326');
    const aerialCoord = new LonLatCoordinate(lonlat)
      .toDataProjection()
      .toArray()
      .slice(0,2);

    const contextData = {
      selectedTags,
      aerialCoord
    } as ContextData;

    // Show default context menu
    showContextMenu(event, true, contextData);
  }

  getFeatureForEvent(event): BaseCameraFeature[] {
    return this.map.getFeaturesAtPixel(event.pixel)
      .filter(feature => feature.get('features'))
      .reduce((previous, current) => [...previous, ...current.get('features')], [])
      .filter(feature =>  feature instanceof BaseCameraFeature);
  }

  handleWindowResize() {
    if (!this.map) {
      return;
    }

    this.map.getRenderer().element_.style.display = "none";
    this.map.updateSize();
    this.map.getRenderer().element_.style.display = "block";

    this.map.render();

  }

  setDragState(state) {
    this.map.interactions.forEach(interaction => {
      if (interaction instanceof DragPan) {
        interaction.setActive(state);
      }
    });
  }

  jumpToPosition(position) {
    const lonlat = new DataProjectionCoordinate([position[0], position[1], 0]).toLonLat();
    const center = transform([lonlat.x, lonlat.y], 'EPSG:4326', 'EPSG:3857');
    const view = this.map.getView();
    view.setCenter(center);
  }

  centerOnImages() {
    if (this.cameraList.count === 0) {
      return;
    }

    const extent = this.pointsSource.getExtent();
    this.setMapExtent(extent);
  }

  addCameraMarkers(cameras: Array<CameraImage>) {
    let newCameras = cameras.filter(x => !this.cameras.includes(x));
    this.cameras = [...this.cameras, ...newCameras];

    // Clear previous data
    this.features = [];
    this.pointsSource.clear();

    // Reset data to calculate
    this.camerasToIterate = this.cameras;
    this.calculating = this.cameras.length > 0;
  }

  calculateCameras() {
    if (this.viewer.calculatingSceneMarkers) return;
    if (this.camerasToIterate.length === 0) return;

    let cameraIndex = 0;
    let numToIterate = this.camerasToIterate.length;
    let camerasCalculated = [];

    let timeStart = performance.now();
    for (cameraIndex = 0; cameraIndex < numToIterate; cameraIndex++) {
      let camera = this.camerasToIterate[cameraIndex];
      let position = camera.position.aerial;
      let point = new Point([position[0], position[1]]);
      camerasCalculated.push({point, camera});

      let elapsed = performance.now() - timeStart;
      if (elapsed > this.maxIterationTime) break;
    }

    this.camerasToIterate = this.camerasToIterate.slice(cameraIndex+1);

    // Add new features
    camerasCalculated.forEach(({point, camera}) => {
      const opts = {
        geometry: point,
        cameraID: camera.id,
        cameraCSV: camera.csv,
        fromJSON: camera.fromJSON
      };

      let feature;
      if (camera instanceof PanoramicImage) {
        feature = new PanoramicFeature(opts);
      } else if (camera instanceof PlanarImage) {
        feature = new PlanarFeature(opts);
      }

      this.features.push(feature);
    });

    if (this.camerasToIterate.length !== 0) return;

    this.pointsSource.clear();
    this.pointsSource.addFeatures(this.features);
    this.calculating = false;

    const updateExtent = this.features
      .filter(x => x.fromJSON).length === 0;

    if (!updateExtent) return;
    this.centerOnImages();
  }

  removeCameraMarkers(cameras: CameraImage[]) {
    const toRemove = new Set(cameras.map(x => x.id));

    const remainingCameras = this.cameras.filter(x => !toRemove.has(x.id));
    this.cameras = [...remainingCameras];

    const remainingFeatures = this.features.filter(x => !toRemove.has(x.cameraID));
    this.features = [...remainingFeatures];
  }

  recalculateAllMarkers() {
    const cameras = [...this.cameras];
    this.removeCameraMarkers(cameras);
    this.addCameraMarkers(cameras);
  }

  styleGenerator(size, active, square = false) {
    const strokeColor = 'rgba(0, 0, 0, 1.0)';
    const textColor = 'rgba(0, 0, 0, 1.0)';
    const fillColor = active
      ? 'rgba(255, 195, 0, 0.8)'
      : 'rgba(255, 255, 255, 0.8)';

    const scale = (1 + 0.25 * Math.log(size));
    const radius = this.clusterCircleSize * scale;

    const circleImage = new CircleStyle({
      radius: radius,
      stroke: new Stroke({
        color: strokeColor
      }),
      fill: new Fill({
        color: fillColor
      }),
    });

    const squareImage = new RegularShapeStyle({
      radius: radius * 1.3,
      stroke: new Stroke({
        color: strokeColor,
        width: Math.sqrt(2.0) * 0.95
      }),
      fill: new Fill({
        color: fillColor
      }),
      angle: Math.PI / 4,
      points: 4
    });

    return new Style({
      image: square ? squareImage : circleImage,
      text: new Text({
        text: size > 1 ? size.toString() : "",
        fill: new Fill({color: textColor}),
      }),
    });
  }

  clusterStyle(feature) {
    let features = feature.get('features');

    let numSquares = 0;
    let numCircles = 0;
    features.forEach(feature => {
      numSquares += (feature instanceof PlanarFeature) ? 1 : 0;
      numCircles += (feature instanceof PanoramicFeature) ? 1 : 0;
    });

    let square = (numSquares > 0) && (numCircles === 0);

    let size = features.length;
    let styleKey = `${features.length}-${square ? "square" : "circle"}`;
    let style = this.styleCache[styleKey];

    if (!style) {
      style = this.styleGenerator(size, false, square);
      this.styleCache[styleKey] = style;
    }
    return style;
  }

  setVisibleBasemap(basemapID) {
    this.basemapLayers.forEach(layer => {
      let id = layer.get("id");
      layer.setVisible(id === basemapID);
    });

    this.updateSavedState();
  }

  setActiveCamera(cameraID = null) {
    let camera = this.cameras.find(x => x.id === cameraID);
    if (!camera) {
      this.activeCamera = null;
      this.activeCameraLayer?.setVisible(false);
      return;
    }

    const mapPosition = camera.position.aerial;
    const point = new Point([mapPosition[0], mapPosition[1]]);
    let feature = this.activeCameraLayer.getSource().getFeatures()[0];
    feature.setGeometry(point);
    this.activeCamera = camera;

    if (camera instanceof PanoramicImage) {
      // Force circle style
      feature.setStyle(this.styleGenerator(1, true, false));
    } else if (camera instanceof PlanarImage) {
      // Force square style
      feature.setStyle(this.styleGenerator(1, true, true));
    }

    this.activeCameraLayer.setVisible(true);
  }

  setMapExtent(extent) {
    const view = this.map.getView();

    view.setRotation(0);
    view.fit(extent, this.map.getSize());
    view.setZoom(view.getZoom() - 1.0);
  }

  getByCSV(assetID) {
    return this.features.filter(feature => feature.cameraCSV === assetID);
  }

  destroy() {
    this.removeEvents();
    this.observer.disconnect();
    this.map.setTarget(null);
  }

  update(changeDetector: ChangeDetector) {
    this.calculateCameras();

    if (this.viewer.orbitState) {
      this.triangle.clear();
      return;
    }

    if (this.viewer.hasPoints || this.viewer.cameraSelected) {
      this.triangle.update(changeDetector);
    }
  }
}

export const extentFromPoints = (positions, reproject=true) => {
  let xPositions = [];
  let yPositions = [];

  if (reproject) {
    positions = positions.map(position => {
      let lonlat = new DataProjectionCoordinate(position).toLonLat();
      let mapPos = transform([lonlat.x, lonlat.y], 'EPSG:4326', 'EPSG:3857');
      return mapPos;
    });
  }

  positions.forEach(position => {
    xPositions.push(position[0]);
    yPositions.push(position[1]);
  });

  let extent = [
    Math.min(...xPositions),
    Math.min(...yPositions),
    Math.max(...xPositions),
    Math.max(...yPositions)
  ];

  return extent;
};
